<template>
    <div>
        <div v-if="activityContent.content" class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content" />
                </e-field>
            </div>
        </div>

        <div v-if="isGenerationStatusUiEnabled && (isGenerating || isFailedGeneration)">
            <activity-generating-placeholder
                activity-type="flashcard"
                :generation-status="generationStatus"
                :generation-error="generationError"
            />
        </div>

        <div v-else-if="flashcardSet && sessionLoaded">
            <flashcard-player
                :cards="playerCards"
                :title="flashcardSet.title"
                :read-only="readOnly"
                :show-card-preview="admin || instructor"
                :session-size="flashcardSet.session_size || null"
                :total-deck-cards="totalDeckCards"
                :rating-mode="fsrsEnabled ? 'fsrs' : 'confidence'"
                :cards-due="cardsDue"
                :due-summary="dueSummary"
                :activity-id="activityId"
                :on-session-start="onSessionStart"
                @complete="onSessionComplete"
                @study-again="onStudyAgain"
                @back="resetView"
            />
        </div>

        <div v-else-if="!flashcardSet && !loading" class="notification is-warning is-light">
            No flashcard set is associated with this activity.
        </div>

        <div v-if="loading" class="has-text-centered p-lg">
            <e-loading :is-full-page="false" active />
        </div>

        <activity-completion-action-row v-if="!loading" />
    </div>
</template>

<script>
import { mapState } from 'vuex';

import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import ActivityGeneratingPlaceholder from '@/components/activity-types/course-activity-types/activities/shared/ActivityGeneratingPlaceholder.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import FlashcardPlayer from '@/components/flashcard-player/FlashcardPlayer.vue';
import { ACTIVITY_GENERATION_STATUS_UI_ENABLED } from '@/constants/activityGenerationStatusFlag.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import axios from '@/plugins/axios.js';
import flashcardService from '@/services/flashcardService.js';
import { loadRendererActivityContent } from '@/utilities/rendererActivityContent.js';

const PENDING_GENERATION_STATUSES = ['pending', 'queued', 'processing', 'generating'];
const POLL_INTERVAL_MS = 15000;

export default {
    name: 'Flashcard',

    components: {
        ActivityGeneratingPlaceholder,
        ActivityCompletionActionRow,
        DisplayContent,
        FlashcardPlayer,
    },

    mixins: [IsLateMixin, CourseActivityMixin],

    props: {
        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        flashcardSet: null,
        sessionCards: [],
        totalDeckCards: 0,
        loading: true,
        sessionLoaded: false,
        sessionPayload: null,
        fsrsEnabled: false,
        sessionToken: null,
        cardsDue: null,
        dueSummary: null,
        generationStatus: null,
        generationError: null,
        statusPollIntervalId: null,
    }),

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        isGenerationStatusUiEnabled: () => ACTIVITY_GENERATION_STATUS_UI_ENABLED,

        activityId() {
            try {
                return this.$store.getters[`${this.namespace}/get`](this.uuid)?.id || null;
            } catch {
                return null;
            }
        },

        playerCards() {
            const cards = this.sessionCards.length ? this.sessionCards : this.flashcardCards;
            const sessionSize = this.flashcardSet?.session_size;
            if (sessionSize && cards.length > sessionSize) {
                return cards.slice(0, sessionSize);
            }
            return cards;
        },

        flashcardCards() {
            if (!this.flashcardSet) return [];

            if (this.flashcardSet.sections && this.flashcardSet.sections.length) {
                return this.flashcardSet.sections.reduce((cards, section) => {
                    const sectionCards = (section.cards || []).map(card => ({
                        ...card,
                        imageUrl: section.image_url || null,
                    }));
                    return cards.concat(sectionCards);
                }, []);
            }

            return this.flashcardSet.cards || [];
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: {
                    ...(this.sessionPayload || { cards_viewed: 0 }),
                    ...(this.sessionToken ? { session_token: this.sessionToken } : {}),
                },
            };
        },

        isGenerating() {
            if (PENDING_GENERATION_STATUSES.includes(this.generationStatus)) return true;
            return !!this.generationStatus && !this.activityContent?.flashcard_set_id;
        },

        isFailedGeneration() {
            return this.generationStatus === 'failed';
        },
    },

    async created() {
        await this.loadActivity();
    },

    beforeUnmount() {
        this.clearStatusPolling();
    },

    methods: {
        async loadActivity({ refresh = false } = {}) {
            try {
                const content = await loadRendererActivityContent({
                    namespace: this.namespace,
                    activityContent: this.activityContent,
                    key: 'flashcard_set',
                    refresh,
                    fetch: async () => {
                        const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
                        const endpoint = this.namespace === 'Activities' ? 'activities' : 'activity-templates';
                        const { data } = await axios.get(
                            `/api/${endpoint}/${id}?include=contentable.flashcardSet.sections.cards,contentable.flashcardSet.sections.media`
                        );

                        return data.data.activity_content;
                    },
                });

                this.generationStatus = content?.generation_status ?? null;
                this.generationError = content?.generation_error ?? null;

                const flashcardSetData = content?.flashcard_set;

                if (flashcardSetData) {
                    this.flashcardSet = flashcardSetData;
                    if (this.namespace === 'Activities') {
                        await this.loadSession();
                    } else {
                        this.totalDeckCards = this.flashcardCards.length;
                    }
                    this.sessionLoaded = true;
                }
            } catch (error) {
                this.$handleResponseError(error);
            } finally {
                this.loading = false;
                this.ensureStatusPolling();
            }
        },

        ensureStatusPolling() {
            if (!this.isGenerating) {
                this.clearStatusPolling();
                return;
            }
            if (this.statusPollIntervalId) return;

            this.statusPollIntervalId = setInterval(() => {
                this.loadActivity({ refresh: true }).catch(() => {});
            }, POLL_INTERVAL_MS);
        },

        clearStatusPolling() {
            if (this.statusPollIntervalId) {
                clearInterval(this.statusPollIntervalId);
                this.statusPollIntervalId = null;
            }
        },
        async onSessionComplete(submissionData) {
            this.sessionPayload = submissionData;

            if (this.namespace === 'Activities' && submissionData.card_responses?.length) {
                try {
                    const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
                    await flashcardService.submitResponses(id, submissionData.card_responses, this.sessionToken);
                } catch (error) {
                    this.$handleResponseError(error);
                    return;
                }
            }

            // Only create an ActivitySubmission on first completion.
            // Subsequent "Study Again" sessions persist FSRS state via the session endpoint above.
            if (!this.completed && !this.readOnly) {
                await this.submit();
            }
        },

        async loadSession() {
            try {
                const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
                const data = await flashcardService.getSession(id);
                this.sessionCards = (data.cards || []).map(card => ({
                    ...card,
                    imageUrl: card.image_url || null,
                }));
                this.totalDeckCards = data.total_cards || this.flashcardCards.length;
                this.fsrsEnabled = data.fsrs_enabled || false;
                // Session token is intentionally NOT set here. It's only obtained when
                // the user clicks "Start Studying" (see onSessionStart).
                this.sessionToken = null;
                this.cardsDue = data.cards_due ?? null;
                this.dueSummary = data.due_summary ?? null;
            } catch (error) {
                console.warn('Flashcard session load failed, falling back to full deck:', error);
                this.sessionCards = [];
                this.totalDeckCards = this.flashcardCards.length;
                this.fsrsEnabled = false;
                this.sessionToken = null;
            }
        },

        async onSessionStart() {
            // Called by FlashcardPlayer when the user clicks "Start Studying".
            // This is the only point where a FlashcardSession row is created.
            if (this.namespace !== 'Activities') {
                return;
            }

            try {
                const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
                const data = await flashcardService.startSession(id, this.playerCards.length);
                this.sessionToken = data.session_token;
                this.fsrsEnabled = data.fsrs_enabled ?? this.fsrsEnabled;
            } catch (error) {
                console.warn('Failed to start flashcard session:', error);
                this.$handleResponseError(error);
            }
        },

        async onStudyAgain() {
            if (this.namespace === 'Activities') {
                await this.loadSession();
            }
        },

        async resetView() {
            if (this.namespace === 'Activities') {
                await this.loadSession();
            }
        },
    },
};
</script>
